import { Input, notification, Typography } from 'antd';
import { deleteDestinationContent } from 'api/destinations.api';
import FormField from 'components/FormField';
import { DestinationContentSources } from 'constants/destination-content-sources';
import { DestinationContentTypes } from 'constants/destination-content-types';
import {
    GUIDE_NUMBER_OF_DAYS_DESCRIPTION_MAX_LENGTH,
    GUIDE_NUMBER_OF_DAYS_TITLE_MAX_LENGTH,
} from 'constants/fields-limits';
import { UpdateTranslationsValue, useDestinationContentTranslation } from 'hooks/use-destination-content-translation';
import type { DestinationContentFieldErrors } from 'hooks/use-destination-content-validation';
import { useDestinationContentValidation } from 'hooks/use-destination-content-validation';
import { useFormDirtyState } from 'hooks/use-form-dirty-state';
import React, { useCallback, useState } from 'react';
import { DestinationContentSchema } from 'typings/destinations/DestinationContentSchema';
import { SortableList } from '../GuideEditor/SortableList';
import { useSort } from '../GuideEditor/SortableList/use-sort';
import type { UpdateDestinationContentFieldChange } from './DestinationEditorContext';
import { useDestinationEditor } from './DestinationEditorContext';
import styles from './DestinationEditorItinerary.module.css';
import { useSyncedDestinationContents } from './use-synced-destination-contents';
import { DestinationEditorTabActions } from './DestinationEditorTabActions';

type DestinationEditorItineraryProps = Readonly<{
    onClickDiscard: (hasUnsavedChanges: boolean) => void;
}>;

export function DestinationEditorItinerary({ onClickDiscard }: DestinationEditorItineraryProps) {
    const { destination, saveBulkContent, updateDestination } = useDestinationEditor();
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [itineraryItems, setItineraryItems] = useSyncedDestinationContents(DestinationContentTypes.ITINERARY);

    const { fieldErrors, validateFields } = useDestinationContentValidation(itineraryItems, ['title', 'body']);

    const { onMoveUp, onMoveDown } = useSort(itineraryItems);

    const { isDirty, updateOriginalValues } = useFormDirtyState({
        values: itineraryItems,
    });

    const handleUpdateFields = (changes: UpdateDestinationContentFieldChange[]) => {
        let updatedItems = itineraryItems;
        for (const change of changes) {
            updatedItems = updatedItems.map((item) =>
                item.id === change.id ? { ...item, [change.fieldName]: change.newValue } : item
            );
        }
        setItineraryItems(updatedItems);

        if (isSubmitted) {
            validateFields(updatedItems);
        }
    };

    const handleUpdateField = (change: UpdateDestinationContentFieldChange) => {
        handleUpdateFields([change]);
    };

    const handleAdd = () => {
        if (!destination.id) {
            return;
        }

        const newItem = getEmptyItineraryItem(destination.id, itineraryItems.length);

        setItineraryItems(itineraryItems.concat([newItem]));
    };

    const handleUpdateTranslationField = ({ contentId, translations }: UpdateTranslationsValue) => {
        setItineraryItems((prev) =>
            prev.map((item) =>
                item.id === contentId
                    ? {
                          ...item,
                          translations,
                      }
                    : item
            )
        );
    };

    const handleDelete = async ({ id }: DestinationContentSchema) => {
        try {
            setIsDeleting(true);

            if (id > 0) {
                await deleteDestinationContent(id);
            }

            setItineraryItems((prev) =>
                prev
                    .filter((item) => item.id !== id)
                    .map((item, index) => ({
                        ...item,
                        order: index,
                    }))
            );
        } catch (error) {
            notification.error({
                message: 'Error deleting itinerary item',
                description: error instanceof Error ? error.message : 'Unknown error',
            });
        } finally {
            setIsDeleting(false);
        }
    };

    const handleMoveUp = (item: DestinationContentSchema) => {
        const sortedItems = onMoveUp(item);

        if (sortedItems) {
            handleUpdateFields(
                sortedItems.map<UpdateDestinationContentFieldChange>((item) => ({
                    id: item.id,
                    fieldName: 'order',
                    newValue: item.newOrder,
                }))
            );
        }
    };

    const handleMoveDown = (item: DestinationContentSchema) => {
        const sortedItems = onMoveDown(item);
        if (sortedItems) {
            handleUpdateFields(
                sortedItems.map<UpdateDestinationContentFieldChange>((item) => ({
                    id: item.id,
                    fieldName: 'order',
                    newValue: item.newOrder,
                }))
            );
        }
    };

    const handleSave = async () => {
        setIsSubmitted(true);

        if (!validateFields(itineraryItems)) {
            notification.error({
                message: 'Please fix the errors before saving',
            });
            return;
        }

        setIsSaving(true);

        try {
            if (!destination.id) {
                throw new Error('Destination ID is required');
            }

            // Bulk save all itinerary items at once
            const savedContents = await saveBulkContent({
                destinationId: destination.id,
                contents: itineraryItems.map((item) => {
                    const contentId = item.id > 0 ? item.id : undefined;
                    return {
                        ...item,
                        id: contentId,
                        translations: item.translations,
                    };
                }),
            });

            // Filter to only get itinerary items (API might return other content types)
            const savedItineraryItems = savedContents.filter(
                (content) => content.content_type === DestinationContentTypes.ITINERARY
            );

            // Update local state with saved items
            setItineraryItems(savedItineraryItems);

            // Update the destination - remove old itinerary items and add saved ones
            const nonItineraryContents = (destination.contents ?? []).filter(
                (content) => content.content_type !== DestinationContentTypes.ITINERARY
            );

            updateDestination({
                ...destination,
                contents: [...nonItineraryContents, ...savedItineraryItems],
            });

            notification.success({
                message: 'Itinerary items saved successfully',
                key: 'itinerary-items-saved',
            });

            // Update original values after successful save
            updateOriginalValues();
        } catch (error) {
            notification.error({
                message: 'Error saving itinerary items',
                description: error instanceof Error ? error.message : 'Unknown error',
            });
        } finally {
            setIsSaving(false);
        }
    };

    if (!destination) {
        return <div>Loading...</div>;
    }

    return (
        <>
            <SortableList
                title='Itinerary'
                data={itineraryItems}
                onAdd={handleAdd}
                onDelete={handleDelete}
                onMoveDown={handleMoveDown}
                onMoveUp={handleMoveUp}
                isLoading={isSaving || isDeleting}>
                {({ data, index }) =>
                    !data ? (
                        <SortableList.EmptyState
                            message='No itinerary yet'
                            action={
                                <SortableList.AddButton onClick={handleAdd} disabled={isSaving}>
                                    Add item
                                </SortableList.AddButton>
                            }
                        />
                    ) : (
                        <DestinationEditorItineraryItem
                            key={data.id}
                            index={index}
                            itineraryItem={data}
                            onChangeField={handleUpdateField}
                            onChangeTranslations={handleUpdateTranslationField}
                            isSubmitted={isSubmitted}
                            errors={fieldErrors[data.id] ?? {}}
                        />
                    )
                }
            </SortableList>
            <DestinationEditorTabActions
                tabKey='itinerary'
                saveLabel='Save Itinerary'
                isSaving={isSaving}
                isSaveDisabled={isDeleting || !isDirty}
                onCancel={() => onClickDiscard(isDirty)}
                onSave={handleSave}
            />
        </>
    );
}

type DestinationEditorItineraryItemProps = {
    index: number;
    itineraryItem: DestinationContentSchema;
    onChangeField: (change: UpdateDestinationContentFieldChange) => void;
    onChangeTranslations: (value: UpdateTranslationsValue) => void;
    isSubmitted?: boolean;
    errors?: DestinationContentFieldErrors;
};

function DestinationEditorItineraryItem({
    index,
    itineraryItem,
    onChangeField,
    onChangeTranslations,
    isSubmitted = false,
    errors = {},
}: DestinationEditorItineraryItemProps) {
    const { locale } = useDestinationEditor();

    const {
        translatedContent: itineraryItemValue,
        isTranslation,
        handleUpdateTranslationField,
    } = useDestinationContentTranslation({
        content: itineraryItem,
        locale,
    });

    const handleChangeField = useCallback(
        <T extends keyof DestinationContentSchema>(fieldName: T, newValue: DestinationContentSchema[T]) => {
            if ((fieldName === 'title' || fieldName === 'body') && isTranslation) {
                onChangeTranslations({
                    contentId: itineraryItem.id,
                    translations: handleUpdateTranslationField({
                        [fieldName]: newValue,
                    }),
                });
            } else {
                onChangeField({
                    id: itineraryItem.id,
                    fieldName: fieldName,
                    newValue: newValue,
                });
            }
        },
        [isTranslation, onChangeTranslations, itineraryItem.id, handleUpdateTranslationField, onChangeField]
    );

    return (
        <div className={styles.item}>
            <Typography.Title level={5}>{`Item ${index + 1}`}</Typography.Title>
            <div className={styles.itemContent}>
                <div className={styles.form}>
                    <FormField
                        label='Title'
                        length={itineraryItemValue.title?.length ?? 0}
                        maxLength={GUIDE_NUMBER_OF_DAYS_TITLE_MAX_LENGTH}
                        labelWidth={100}
                        className={styles.noMargin}
                        error={isSubmitted ? errors.title : undefined}>
                        <Input
                            size='large'
                            value={itineraryItemValue.title ?? ''}
                            status={isSubmitted && errors.title ? 'error' : ''}
                            onChange={(e) => handleChangeField('title', e.target.value)}
                        />
                    </FormField>

                    <FormField
                        label='Description'
                        length={itineraryItemValue.body?.length ?? 0}
                        maxLength={GUIDE_NUMBER_OF_DAYS_DESCRIPTION_MAX_LENGTH}
                        labelWidth={100}
                        className={styles.noMargin}
                        error={isSubmitted ? errors.body : undefined}>
                        <Input.TextArea
                            size='large'
                            value={itineraryItemValue.body ?? ''}
                            autoSize={{
                                minRows: 1,
                                maxRows: 4,
                            }}
                            status={isSubmitted && errors.body ? 'error' : ''}
                            onChange={(e) => handleChangeField('body', e.target.value)}
                        />
                    </FormField>
                </div>
            </div>
        </div>
    );
}

function getEmptyItineraryItem(destinationId: number, order: number): DestinationContentSchema {
    return {
        // Use negative id to ensure new highlights are added at the end, this is needed because the List component uses id as the key
        id: -order,
        destination_id: destinationId,
        content_type: DestinationContentTypes.ITINERARY,
        order,
        body: '',
        source: DestinationContentSources.MANUAL,
        title: '',
        cms_media: null,
        translations: [],
    };
}
